import { useEffect, useState } from 'react';
import { SupportCasesItem } from '../../../../types';
import { Text } from '../../../Atoms/Text';
import { DetailViewCard } from '../../../Compounds/CardWithTitle';
import { getCaseLoadingSubject } from '../../../../store/SupportCase';
import { AttachmentTable } from '../../../Compounds/AttachmentTable';
import { ComponentSpinner } from '../../../Compounds/Loading/ComponentSpinner';
import { CaseInventoryTable } from './CaseInventoryTable';
import { sanitizeDocumentHtml } from '../../../../utils/documentHtml';
import { useHtmlPreview } from '../../../../utils/htmlPreview';

export interface SupportCaseDetailsProps {
  data: SupportCasesItem;
}

export function SupportCaseDetails({ data }: SupportCaseDetailsProps) {
  const [casesLoading, setCasesLoading] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(false);
  const { dateTimeOpened, dateTimeClosed, priority, status, subject, caseType, issue, submittedBy, description } = data;
  const { isLong, content } = useHtmlPreview(description || '', sanitizeDocumentHtml, showMore);

  useEffect(() => {
    const caseLoadingSub = getCaseLoadingSubject().subscribe((loading: boolean) => {
      setCasesLoading(loading);
    });

    return () => {
      if (caseLoadingSub) caseLoadingSub.unsubscribe();
    };
  }, []);

  return (
    <>
      <DetailViewCard
        collapsed={false}
        title="Details">
        <div className="px-5">
          {casesLoading && (
            <div className="mt-6">
              <ComponentSpinner />
            </div>
          )}
          {!casesLoading && (
            <>
              <div className="flex">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Subject'}
                </Text>
                <Text
                  className="font-sans font-normal w-2/3"
                  size="sm">
                  {subject}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Status'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {status}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Case Type'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {caseType}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Issue'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {issue}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Priority'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {priority}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Submitted By'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {submittedBy}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Date/Time Opened'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {dateTimeOpened}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Date/Time Closed'}
                </Text>
                <Text
                  className="font-sans font-normal"
                  size="sm">
                  {dateTimeClosed}
                </Text>
              </div>

              <div className="flex mt-10">
                <Text
                  className="font-sans text-grey-4 w-1/3"
                  size="sm14">
                  {'Description'}
                </Text>
              </div>
              {description && (
                <div className="mt-2">
                  {/* Descriptions that come by email or from Salesforce carry markup, so they render as HTML. */}
                  <div className="text-black text-sm ql-editor p-1 border overflow-x-auto [&_img]:max-w-full [&_img]:h-auto [&_table]:max-w-full">
                    {content}
                  </div>
                  {isLong && (
                    <button
                      aria-expanded={showMore}
                      onClick={() => setShowMore(!showMore)}>
                      <Text
                        size="sm"
                        color="indigoMid">
                        {showMore ? 'Show Less' : 'Show More'}
                      </Text>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DetailViewCard>
      {data.id && <CaseInventoryTable caseId={data.id} />}
      {data.id && (
        <AttachmentTable
          id={data.id}
          parentType="Case"
        />
      )}
    </>
  );
}
